import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { formatPhone } from '../../utils/phone';
import { CertsPanel } from '../../components/EmployeeCerts';
import {
  fetchEmployees,
  createEmployee,
  updateEmployee,
  deactivateEmployee,
  setEmployeePin,
  setStaffCredentials,
  setMailCredentials,
  testMailCredentials,
  ROLES,
  STAFF_ROLES,
  ROLE_LABELS,
} from '../../services/employees';

const EMPTY_EMP = {
  id: '', firstName: '', lastName: '', email: '',
  phone: '', role: 'Worker', status: 'active',
  isCdl: 0, isShop: 0, username: '', newPassword: '', trade: '',
  address: '', city: '', state: '', zip: '',
};

const API_BASE = process.env.REACT_APP_API_BASE || 'https://app.titanelectricreno.com:8080/api';
function getToken() {
  try { return JSON.parse(localStorage.getItem('titan_session') || 'null')?.token; } catch { return null; }
}
function apiFetch(path, opts = {}) {
  const token = getToken();
  return fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...(token ? { 'x-worker-token': token } : {}), ...opts.headers },
  }).then(r => r.json());
}
// Executives edit anyone. The office (Accounting) edits everyone but the Executives and
// cannot make someone an Executive (Ryan, Oct 2 2026). The server enforces both; this
// keeps the buttons off everyone else's screen so the page reads as the directory it is.
function sessionRole() {
  try { const s = JSON.parse(localStorage.getItem('titan_session') || 'null'); return s?.user?.role || s?.role || null; } catch { return null; }
}
const IS_EXEC_VIEWER = ['Executive', 'Admin', 'admin'].includes(sessionRole());
const CAN_EDIT_EMPLOYEES = IS_EXEC_VIEWER || sessionRole() === 'Accounting';
const canEditEmployee = emp => IS_EXEC_VIEWER || (CAN_EDIT_EMPLOYEES && !['Executive', 'Admin', 'admin'].includes(String(emp?.role || '')));
// Adding a person is the office's job too (Pat, Sep 25 2026). Employee files and edits
// stay with the Executives; the office cannot make someone an Executive.
const CAN_ADD_EMPLOYEES = CAN_EDIT_EMPLOYEES || sessionRole() === 'Accounting';

// One row in the directory table.
// Clicking a row opens the person's card (Executives/Admins). The old accordion under
// each row listed every project the person had ever been a member of — mostly the
// Trello-era board memberships — which Ryan said nobody needs any more (Sep 2026).
// Fieldwire files a post from the app under the person only when their app email is a
// project member's email (Ryan, Sep 28 2026). This says who is not lined up, and — when
// Fieldwire has someone by the same name under another address — offers that address.
const nameKey = s => String(s || '').toLowerCase().replace(/[^a-z]/g, '');
function fwMatchFor(emp, people) {
  if (!people || !people.length) return null;
  const em = String(emp.email || '').toLowerCase().trim();
  if (em && people.some(p => p.email === em)) return { ok: true };
  const fn = nameKey(emp.firstName), ln = nameKey(emp.lastName);
  const byName = ln ? people.find(p => nameKey(p.last_name) === ln && (nameKey(p.first_name) === fn || (fn.length >= 3 && nameKey(p.first_name).startsWith(fn.slice(0, 3))))) : null;
  return { ok: false, suggestion: byName ? byName.email : null };
}

// "6717 Rolling Meadows Dr, Apt 1821, Sparks, NV 89436" — or '' when nothing is on file.
const addressLine = e => [e.address, e.city, [e.state, e.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');

function EmployeeRow({ emp, onEdit, onDelete, onPoToggle, canEdit = false, fw = null, onUseEmail }) {
  const [poEnabled, setPoEnabled] = useState(emp.canCreatePo !== false);

  return (
    <>
      <tr
        className="emp-row"
        onClick={() => { if (canEdit) onEdit(emp); }}
        style={{ cursor: canEdit ? 'pointer' : 'default' }}
        title={canEdit ? 'Open this person’s card' : undefined}
      >
        <td>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div className="member-avatar" style={{ width: 30, height: 30, fontSize: 10, flexShrink: 0 }}>
              <span>{emp.firstName?.[0]}{emp.lastName?.[0]}</span>
            </div>
            <span style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>{emp.firstName} {emp.lastName}</span>
          </div>
        </td>
        <td><span className="admin-tag" style={{ whiteSpace: 'nowrap' }}>{ROLE_LABELS[emp.role] || emp.role || '—'}</span>{emp.trade && <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2, whiteSpace: 'nowrap' }} title="Trade — bills as this on a Rate Sheet work order">🔧 {emp.trade}</div>}</td>
        <td style={{ color: 'var(--gray-muted)', fontSize: 13, whiteSpace: 'nowrap' }}>{formatPhone(emp.phone) || '—'}</td>
        {/* Home address (Ryan, Nov 2 2026): one line, and a map link. Typed on the card or brought in from the payroll sheet. */}
        <td style={{ fontSize: 12.5, color: '#374151', maxWidth: 260 }} onClick={e => e.stopPropagation()}>
          {addressLine(emp)
            ? <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressLine(emp))}`} target="_blank" rel="noreferrer" title="Open in Maps"
                style={{ color: '#374151', textDecoration: 'none' }}>📍 {addressLine(emp)}</a>
            : <span style={{ color: '#c4c4c4' }}>—</span>}
        </td>
        <td onClick={e => e.stopPropagation()}>
          {/* PINs are stored hashed and can't be read back — set a new one on the card
              and tell the person. */}
          <span className="admin-tag" title={emp.hasPin ? 'A PIN is set — open the card to give them a new one' : 'No PIN — this person signs in with a username and password, or needs one set'}
            style={{ color: emp.hasPin ? '#15803d' : '#9ca3af' }}>
            {emp.hasPin ? 'PIN set' : 'no PIN'}
          </span>
        </td>
        <td style={{ fontSize: 13 }} onClick={e => e.stopPropagation()}>
          <a href={`mailto:${emp.email}`} style={{ color: 'var(--orange)', textDecoration: 'none' }}>{emp.email || '—'}</a>
          {fw && !fw.ok && emp.status === 'active' && (
            <div style={{ marginTop: 3, fontSize: 11, color: '#92400e', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}
              title="Posts this person makes to Fieldwire from the app are filed under a stand-in until the app email matches their Fieldwire login">
              <span>⚠ Not in Fieldwire under this email{fw.suggestion ? ` · Fieldwire has ${fw.suggestion}` : ' · not on any Fieldwire project'}</span>
              {fw.suggestion && canEdit && onUseEmail && (
                <button onClick={() => onUseEmail(emp, fw.suggestion)}
                  style={{ padding: '1px 8px', borderRadius: 999, border: '1px solid #fcd34d', background: '#fffbeb', color: '#92400e', fontSize: 11, fontWeight: 800, cursor: 'pointer' }}>
                  Use it
                </button>
              )}
            </div>
          )}
        </td>
        <td>
          <span className={`admin-list-badge ${emp.status === 'active' ? 'orange' : emp.status === 'on_leave' ? 'blue' : 'gray'}`}>
            {emp.status === 'active' ? 'Active' : emp.status === 'on_leave' ? 'On Leave' : 'Inactive'}
          </span>
        </td>
        <td onClick={e => e.stopPropagation()}>
          <button disabled={!canEdit}
            title={!canEdit ? (poEnabled ? 'Can create POs' : 'Cannot create POs') : poEnabled ? 'Disable PO creation' : 'Enable PO creation'}
            onClick={async () => {
              if (!canEdit) return;
              const next = !poEnabled;
              setPoEnabled(next);
              await apiFetch(`/users/${emp.id}/can-create-po`, { method: 'PATCH', body: JSON.stringify({ enabled: next }) });
              onPoToggle?.(emp.id, next);
            }}
            style={{ width: 28, height: 28, borderRadius: '50%', border: 'none', cursor: canEdit ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center', background: poEnabled ? '#16a34a' : '#e5e7eb', color: poEnabled ? '#fff' : '#6b7280', fontSize: 15, fontWeight: 700, transition: 'all 0.15s' }}
          >
            {poEnabled ? '✓' : '✕'}
          </button>
        </td>
        <td onClick={e => e.stopPropagation()}>
          {canEdit && (
          <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
            <button className="admin-icon-btn" onClick={() => onEdit(emp)} title="Edit">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M10 2l2 2-8 8H2v-2L10 2Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" fill="none" /></svg>
            </button>
            <button className="admin-icon-btn danger" onClick={() => onDelete(emp.id)} title="Deactivate">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 4h10M5 4V2h4v2M6 7v4M8 7v4M3 4l1 8h6l1-8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" fill="none" /></svg>
            </button>
          </div>
          )}
        </td>
      </tr>
    </>
  );
}

// ── Email from the app — this person's own mailbox ──────────────────────────────
// Saved on its own button, not with the rest of the card: the password goes up once,
// is stored encrypted, and never comes back — the box only ever says "set" or not.
// Test sends a message to the person's own address from the app, as them.
function MailboxBox({ emp, onChanged, showToast }) {
  const [mailUser, setMailUser] = useState(emp.mailUser || emp.email || '');
  const [mailPass, setMailPass] = useState('');
  const [busy, setBusy] = useState('');
  const [note, setNote] = useState('');
  const set = emp.hasMail;
  async function save() {
    if (!mailUser.trim() || (!mailPass && !set)) { setNote('Enter the mailbox address and its password.'); return; }
    setBusy('save'); setNote('');
    try {
      const r = await setMailCredentials(emp.id, { mailUser: mailUser.trim(), mailPass });
      onChanged({ hasMail: r.hasMail, mailUser: r.mailUser || mailUser.trim(), mailSetAt: new Date().toISOString() });
      setMailPass(''); setNote('Saved. Press Test to make sure the login works.'); showToast('Mailbox saved');
    } catch (e) { setNote(e.message); }
    setBusy('');
  }
  async function clear() {
    if (!window.confirm('Remove this person’s mailbox from the app? Their emails will go out from the company mailbox instead.')) return;
    setBusy('clear'); setNote('');
    try { await setMailCredentials(emp.id, { mailUser: '', mailPass: '' }); onChanged({ hasMail: false, mailUser: null, mailSetAt: null }); setMailPass(''); setNote('Cleared.'); }
    catch (e) { setNote(e.message); }
    setBusy('');
  }
  async function test() {
    setBusy('test'); setNote('');
    try { const r = await testMailCredentials(emp.id); setNote(`Login works — a test message was sent to ${r.sent_to}.`); }
    catch (e) { setNote(e.message); }
    setBusy('');
  }
  return (
    <div className="field-group" style={{ border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px', background: 'var(--gray-bg, #f9fafb)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <label className="field-label" style={{ margin: 0, flex: 1 }}>✉️ Email from the app</label>
        <span style={{ fontSize: 11, fontWeight: 800, padding: '2px 8px', borderRadius: 20, background: set ? (emp.mailBroken ? '#fee2e2' : '#dcfce7') : '#f3f4f6', color: set ? (emp.mailBroken ? '#b91c1c' : '#15803d') : '#6b7280' }}>
          {set ? (emp.mailBroken ? 'Password stopped working' : `Set${emp.mailSetAt ? ' · ' + new Date(emp.mailSetAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}`) : 'Not set'}
        </span>
      </div>
      {set && emp.mailBroken && (
        <div style={{ fontSize: 11.5, color: '#b91c1c', marginBottom: 6 }}>The mail server refused this login{emp.mailFailReason ? ` (${emp.mailFailReason})` : ''}. Their emails go out from the company mailbox until a new password is entered — they're asked on their next sign-in, or enter it here.</div>
      )}
      <span style={{ fontSize: 11, color: 'var(--gray-muted)', display: 'block', marginBottom: 8 }}>
        Bids, invoices and other papers this person emails from the app go out from this mailbox, and replies come back to them. Their Kerio login — the password is stored encrypted and never shown again.
      </span>
      <div className="admin-form-row">
        <div className="field-group" style={{ margin: 0 }}>
          <input className="field-input" type="email" autoComplete="off" placeholder="mailbox@titanelectric.biz" value={mailUser} onChange={e => setMailUser(e.target.value)} />
        </div>
        <div className="field-group" style={{ margin: 0 }}>
          <input className="field-input" type="password" autoComplete="new-password" placeholder={set ? 'New password (blank keeps current)' : 'Mailbox password'} value={mailPass} onChange={e => setMailPass(e.target.value)} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 8 }}>
        <button type="button" className="admin-primary-btn" disabled={!!busy} onClick={save} style={{ padding: '7px 14px', fontSize: 13 }}>{busy === 'save' ? 'Saving…' : set ? 'Update mailbox' : 'Save mailbox'}</button>
        {set && <button type="button" className="admin-secondary-btn" disabled={!!busy} onClick={test} style={{ padding: '7px 14px', fontSize: 13 }}>{busy === 'test' ? 'Testing…' : 'Test'}</button>}
        {set && <button type="button" disabled={!!busy} onClick={clear} style={{ background: 'none', border: 'none', color: 'var(--gray-muted)', fontSize: 12.5, cursor: 'pointer', textDecoration: 'underline' }}>Remove</button>}
      </div>
      {note && <div style={{ fontSize: 12.5, color: /works|Saved|Cleared/.test(note) ? '#15803d' : '#b91c1c', fontWeight: 600, marginTop: 6 }}>{note}</div>}
    </div>
  );
}

export default function AdminEmployees() {
  const [employees, setEmployees]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [loadError, setLoadError]   = useState(null);
  const [showForm, setShowForm]     = useState(false);
  const [editing, setEditing]       = useState(null);
  const [form, setForm]             = useState(EMPTY_EMP);
  const [search, setSearch]         = useState('');
  const [roleFilter, setRoleFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('active');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [toast, setToast]           = useState(null);
  const [saving, setSaving]         = useState(false);
  const [originalPin, setOriginalPin] = useState(null);
  const [fwPeople, setFwPeople] = useState([]);     // everyone Fieldwire knows, for the email note
  const [fwOnly, setFwOnly] = useState(false);      // show only the mismatches
  useEffect(() => { apiFetch('/fieldwire/people').then(d => setFwPeople(d?.people || [])).catch(() => setFwPeople([])); }, []);
  // The Labor rows of the Job Rate Sheet — the trades a person can bill as (Ryan, Sep 29 2026).
  const [laborTrades, setLaborTrades] = useState([]);
  useEffect(() => { apiFetch('/rate-sheet').then(d => setLaborTrades((d?.items || []).filter(i => i.category === 'Labor'))).catch(() => setLaborTrades([])); }, []);
  // Trades people already have that aren't Labor rows on the Rate Sheet (Operator Apprentice…), plus the open card's own.
  const otherTrades = [...new Set([...employees.map(e => e.trade), form.trade].filter(t => t && !laborTrades.some(r => r.description === t)))].sort();
  async function useFieldwireEmail(emp, email) {
    try {
      await apiFetch(`/users/${emp.id}`, { method: 'PATCH', body: JSON.stringify({ email }) });
      showToast(`${emp.firstName}'s app email is now ${email}`);
      await load();
    } catch (err) { showToast(err.message || 'Could not change the email.'); }
  }

  const SUB_JOB_TYPES = ['NVE / Power Company', 'Horizontal Drilling', 'Vertical Drilling', 'Shop / Trucking', 'General', 'Loops'];

  useEffect(() => {
    load();
  }, []);

  async function load() {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await fetchEmployees();
      setEmployees(data);
    } catch (e) {
      setLoadError(e.message);
    } finally {
      setLoading(false);
    }
  }

  function openAdd() {
    setForm(EMPTY_EMP);
    setEditing(null);
    setOriginalPin(null);
    setShowForm(true);
  }

  function openEdit(emp) {
    setForm({ ...emp });
    setEditing(emp.id);
    setOriginalPin(emp.pin || '');
    setShowForm(true);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await updateEmployee(editing, form);
        if (form.pin && form.pin !== originalPin) await setEmployeePin(editing, form.pin);
        if (STAFF_ROLES.includes(form.role) && (form.username || form.newPassword)) {
          await setStaffCredentials(editing, { username: form.username || undefined, password: form.newPassword || undefined });
        }
        showToast('Employee updated');
      } else {
        const created = await createEmployee(form);
        if (STAFF_ROLES.includes(form.role) && (form.username || form.newPassword)) {
          await setStaffCredentials(created.id, { username: form.username || undefined, password: form.newPassword || undefined });
        }
        showToast('Employee added');
      }
      setShowForm(false);
      await load();
    } catch (err) {
      showToast(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    try {
      await deactivateEmployee(id);
      setDeleteConfirm(null);
      showToast('Employee deactivated');
      await load();
    } catch (err) {
      showToast(err.message);
    }
  }

  function showToast(msg) {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  }

  const roleCounts = ROLES.reduce((acc, r) => {
    acc[r] = employees.filter(e => e.role === r && (statusFilter === 'all' || e.status === statusFilter)).length;
    return acc;
  }, {});

  const filtered = employees.filter(e => {
    const matchStatus = statusFilter === 'all' || e.status === statusFilter;
    const matchRole = roleFilter === 'All' || e.role === roleFilter;
    const matchSearch = `${e.firstName} ${e.lastName} ${e.email} ${e.role}`
      .toLowerCase().includes(search.toLowerCase());
    const matchFw = !fwOnly || fwMatchFor(e, fwPeople)?.ok === false;
    return matchStatus && matchRole && matchSearch && matchFw;
  });
  const fwMismatches = fwPeople.length ? employees.filter(e => e.status === 'active' && fwMatchFor(e, fwPeople)?.ok === false).length : 0;

  const linked   = employees.filter(e => e.trelloId).length;
  const unlinked = employees.filter(e => !e.trelloId).length;

  return (
    <AdminLayout>
      {toast && <div className="toast">{toast}</div>}

      <div className="admin-page-header">
        <div>
          <Link to="/admin/employees" style={{ color: '#6b7280', fontSize: 13, fontWeight: 600, textDecoration: 'none', display: 'block', marginBottom: 4 }}>← Employees</Link>
          <h1 className="admin-page-title">Employee List</h1>
          <p className="admin-page-sub">{employees.length} employees · {employees.filter(e => e.status === 'active').length} active</p>
        </div>
        {/* The one-time "Addresses from a sheet" and "Trades from QuickBooks Time" buttons came off on Nov 2 2026
            (Ryan: everything is in now). The routes behind them are still there if they are ever wanted back. */}
        {/* The same Add employee button for the office as for the Executives (Pat, Sep 25 2026). */}
        {CAN_ADD_EMPLOYEES && (
        <button className="admin-primary-btn" onClick={openAdd}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M7 1v12M1 7h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          Add employee
        </button>
        )}
      </div>

      {loadError && (
        <div className="mock-banner" style={{ borderColor: 'var(--red)', color: 'var(--red)' }}>
          Could not load employees — {loadError}
        </div>
      )}

      <div className="admin-card">
        <div className="admin-card-header">
          <h2 className="admin-card-title">Directory ({filtered.length})</h2>
          <div className="admin-search-wrap">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--gray-muted)' }}>
              <circle cx="6" cy="6" r="4.5" stroke="currentColor" strokeWidth="1.3" fill="none" />
              <path d="M9.5 9.5l3 3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
            </svg>
            <input className="admin-search" placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 6, padding: '0 20px 12px', borderBottom: '1px solid var(--border)' }}>
          {[['active', 'Active', employees.filter(e => e.status === 'active').length],
            ['on_leave', 'On Leave', employees.filter(e => e.status === 'on_leave').length],
            ['inactive', 'Inactive', employees.filter(e => e.status === 'inactive').length],
            ['all', 'All', employees.length]].map(([val, label, count]) => (
            <button key={val} className={`role-tab ${statusFilter === val ? 'active' : ''}`}
              onClick={() => { setStatusFilter(val); setRoleFilter('All'); }}>
              {label} ({count})
            </button>
          ))}
          {fwPeople.length > 0 && (
            <button onClick={() => setFwOnly(v => !v)} title="People whose app email is not a Fieldwire login — their posts to Fieldwire are filed under a stand-in"
              style={{ marginLeft: 'auto', padding: '6px 12px', borderRadius: 20, border: `1.5px solid ${fwOnly ? '#b45309' : '#fcd34d'}`, background: fwOnly ? '#b45309' : '#fffbeb', color: fwOnly ? '#fff' : '#92400e', fontWeight: 700, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              ⚠ Fieldwire email mismatch ({fwMismatches})
            </button>
          )}
        </div>

        <div className="role-tabs">
          <button className={`role-tab ${roleFilter === 'All' ? 'active' : ''}`} onClick={() => setRoleFilter('All')}>
            All ({employees.filter(e => statusFilter === 'all' || e.status === statusFilter).length})
          </button>
          {ROLES.filter(r => roleCounts[r] > 0).map(r => (
            <button key={r} className={`role-tab ${roleFilter === r ? 'active' : ''}`} onClick={() => setRoleFilter(r)}>
              {ROLE_LABELS[r] || r} ({roleCounts[r]})
            </button>
          ))}
        </div>
        {loading && <div className="admin-empty">Loading…</div>}
        {!loading && filtered.length === 0 && <div className="admin-empty">No employees found.</div>}

        {!loading && filtered.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Name</th><th>Role</th><th>Phone</th><th>Address</th><th>PIN</th>
                  <th>Email</th><th>Status</th><th>Create PO</th><th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(emp => (
                  <EmployeeRow
                    key={emp.id}
                    emp={emp}
                    canEdit={canEditEmployee(emp)}
                    fw={fwMatchFor(emp, fwPeople)}
                    onUseEmail={useFieldwireEmail}
                    onEdit={openEdit}
                    onDelete={id => setDeleteConfirm(id)}
                    onPoToggle={(id, val) => setEmployees(es => es.map(e => e.id === id ? { ...e, canCreatePo: val } : e))}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showForm && (
        <div className="admin-modal-overlay" onClick={() => setShowForm(false)}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>{editing ? 'Edit employee' : 'Add employee'}</h3>
              <button className="admin-modal-close" onClick={() => setShowForm(false)}>✕</button>
            </div>
            <form onSubmit={handleSubmit} className="admin-modal-form">
              <div className="admin-form-row">
                <div className="field-group">
                  <label className="field-label">First name</label>
                  <input className="field-input" required value={form.firstName} onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))} />
                </div>
                <div className="field-group">
                  <label className="field-label">Last name</label>
                  <input className="field-input" required value={form.lastName} onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))} />
                </div>
              </div>
              <div className="field-group">
                <label className="field-label">Email</label>
                <input className="field-input" type="email" value={form.email || ''} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
              </div>
              {/* Home address (Ryan, Nov 2 2026) */}
              <div className="field-group">
                <label className="field-label">📍 Home address</label>
                <input className="field-input" value={form.address || ''} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} placeholder="Street, apt" />
              </div>
              <div className="admin-form-row">
                <div className="field-group" style={{ flex: 2 }}>
                  <label className="field-label">City</label>
                  <input className="field-input" value={form.city || ''} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} />
                </div>
                <div className="field-group">
                  <label className="field-label">State</label>
                  <input className="field-input" value={form.state || ''} onChange={e => setForm(f => ({ ...f, state: e.target.value }))} placeholder="NV" />
                </div>
                <div className="field-group">
                  <label className="field-label">Zip</label>
                  <input className="field-input" value={form.zip || ''} onChange={e => setForm(f => ({ ...f, zip: e.target.value }))} />
                </div>
              </div>
              <div className="admin-form-row">
                <div className="field-group">
                  <label className="field-label">Phone</label>
                  <input className="field-input" type="tel" value={form.phone || ''} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                    onBlur={e => setForm(f => ({ ...f, phone: formatPhone(e.target.value) }))} placeholder="(775) 555-1234" />
                </div>
                <div className="field-group">
                  <label className="field-label">Role</label>
                  <select className="field-input" value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))}>
                    {ROLES.filter(r => IS_EXEC_VIEWER || r !== 'Executive').map(r => <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>)}
                  </select>
                </div>
              </div>

              <div className="field-group">
                {/* The 🔧 trade on the list, picked here (Ryan, Oct 31 2026) — the Rate Sheet's Labor rows
                    plus every trade someone already has, so it no longer depends on the QuickBooks Time pull. */}
                <label className="field-label">🔧 Trade</label>
                <select className="field-input" value={form.trade || ''} onChange={e => setForm(f => ({ ...f, trade: e.target.value }))}>
                  <option value="">— none —</option>
                  {laborTrades.map(t => <option key={t.id} value={t.description}>{t.description}{t.rate != null ? ` · $${Number(t.rate).toLocaleString('en-US')}/hr` : ''}</option>)}
                  {otherTrades.map(t => <option key={t} value={t}>{t} (not on the Rate Sheet)</option>)}
                </select>
                <span style={{ fontSize: 11, color: 'var(--gray-muted)' }}>The Labor rows of the Job Rate Sheet. On a work order priced by the Rate Sheet, this person's hours bill at that row.</span>
              </div>
              <div className="field-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>
                  <input type="checkbox" checked={!!Number(form.isCdl)} onChange={e => setForm(f => ({ ...f, isCdl: e.target.checked ? 1 : 0 }))} />
                  CDL Driver
                </label>
                <span style={{ fontSize: 11, color: 'var(--gray-muted)' }}>Required to do daily pre-trip inspections and log bulk fuel.</span>
              </div>

              {editing && (
                <div className="field-group">
                  <label className="field-label">Trello link</label>
                  <div className="field-input" style={{ background: 'var(--gray-bg)', color: 'var(--gray-muted)', cursor: 'not-allowed' }}>
                    {form.trelloId ? `Linked (${form.trelloId})` : 'Not linked'}
                  </div>
                  <span style={{ fontSize: 11, color: 'var(--gray-muted)' }}>
                    Trello linking is automatic — managed by the sync process, not editable here.
                  </span>
                </div>
              )}

              <div className="field-group">
                <label className="field-label">PIN (4 digits)</label>
                <input
                  className="field-input"
                  type="text"
                  inputMode="numeric"
                  maxLength={4}
                  pattern="\d{4}"
                  placeholder={editing ? '••••' : ''}
                  value={form.pin || ''}
                  onChange={e => {
                    const digits = e.target.value.replace(/\D/g, '').slice(0, 4);
                    setForm(f => ({ ...f, pin: digits }));
                  }}
                />
                <span style={{ fontSize: 11, color: 'var(--gray-muted)' }}>
                  {editing
                    ? 'Leave blank to keep existing PIN.'
                    : (STAFF_ROLES.includes(form.role)
                        ? 'Optional — lets this office user also log in by PIN on their phone.'
                        : 'Required to log in as this worker.')}
                </span>
              </div>

              {STAFF_ROLES.includes(form.role) && (
                <>
                  <div className="admin-form-row">
                    <div className="field-group">
                      <label className="field-label">Username</label>
                      <input
                        className="field-input"
                        type="text"
                        autoComplete="off"
                        placeholder="e.g. jsmith"
                        value={form.username || ''}
                        onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
                      />
                    </div>
                    <div className="field-group">
                      <label className="field-label">{editing ? 'New password' : 'Password'}</label>
                      <input
                        className="field-input"
                        type="password"
                        autoComplete="new-password"
                        placeholder={editing ? 'Leave blank to keep current' : 'Set a password'}
                        value={form.newPassword || ''}
                        onChange={e => setForm(f => ({ ...f, newPassword: e.target.value }))}
                      />
                    </div>
                  </div>
                  <span style={{ fontSize: 11, color: 'var(--gray-muted)', display: 'block', marginTop: -8, marginBottom: 8 }}>
                    Staff users log in via "Staff login" on the login screen using these credentials.
                    {editing && form.hasPassword && !form.newPassword ? ' Password is already set.' : ''}
                  </span>
                  {editing && <MailboxBox emp={form} onChanged={patch => setForm(f => ({ ...f, ...patch }))} showToast={showToast} />}
                </>
              )}

              {/* Certifications (Pat, Oct 5 2026): license, OSHA, CPR… pictures + dates, approve uploads. */}
              {editing && canEditEmployee(form) && (
                <div className="field-group">
                  <label className="field-label">🪪 Certifications</label>
                  <CertsPanel userId={editing} compact />
                </div>
              )}

              <div className="field-group">
                <label className="field-label">Status</label>
                <select className="field-input" value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                  <option value="active">Active</option>
                  <option value="on_leave">On Leave</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              <div className="admin-modal-actions">
                <button type="button" className="admin-secondary-btn" onClick={() => setShowForm(false)}>Cancel</button>
                <button type="submit" className="admin-primary-btn" disabled={saving}>
                  {saving ? 'Saving…' : editing ? 'Save changes' : 'Add employee'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div className="admin-modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="admin-modal" style={{ maxWidth: 360 }} onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Deactivate employee?</h3>
              <button className="admin-modal-close" onClick={() => setDeleteConfirm(null)}>✕</button>
            </div>
            <p style={{ padding: '16px 20px', color: 'var(--gray-muted)', fontSize: 14 }}>
              Sets their status to inactive. They'll no longer appear in the login picker, but their tasks, comments, and time entries are kept.
            </p>
            <div className="admin-modal-actions" style={{ padding: '0 20px 20px' }}>
              <button className="admin-secondary-btn" onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button className="admin-primary-btn" style={{ background: 'var(--red)' }} onClick={() => handleDelete(deleteConfirm)}>Deactivate</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
